import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppShell from '../../components/layout/AppShell';
import HelpButton from '../Guides/HelpButton';
import { LoadingSpinner, EmptyState } from '../../components/layout/SharedComponents';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import InteractiveTable from '../../components/InteractiveTable';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatTimestamp as fmtTs } from '../Requisitions/constants';
import {
  FileQuestion, Plus, Copy, Trash2, Send, Lock, Unlock, FileText, BarChart3, Eye, Search, Gavel,
} from 'lucide-react';
import { TYPE_LABELS, TYPE_COLORS, STATUS_COLORS, TYPE_DESCRIPTIONS } from './constants';
import { RFxEditDialog } from './RFxEditDialog';
import ModuleReports from '../Reports/ModuleReports';
import { RFX_REPORTS } from '../Reports/reportConfigs';
import RFxDashboard from './RFxDashboard';

/** Match the urgency heatmap used on the public tenders portal:
 * green far-out → yellow → orange → red as the closing date approaches.
 * Returns { bg, pct, label } where pct is how far through a 30-day window
 * we are (used for the progress shading). */
function getUrgencyColor(closingDate) {
  if (!closingDate) return { bg: '#a1a1aa', pct: 0, label: 'No closing date' };
  const now = Date.now();
  const close = new Date(closingDate).getTime();
  const remaining = close - now;
  if (remaining <= 0) return { bg: '#ef4444', pct: 100, label: 'Expired' };
  const days = Math.ceil(remaining / (24 * 3600 * 1000));
  const totalWindow = 30 * 24 * 3600 * 1000;
  const pct = Math.min(100, Math.max(0, ((totalWindow - remaining) / totalWindow) * 100));
  const label = days <= 1 ? 'Closing today' : days <= 3 ? `Closing in ${days} days — urgent` : `Closing in ${days} days`;
  if (pct < 40) return { bg: `hsl(${120 - pct * 1.5}, 70%, 45%)`, pct, label };
  if (pct < 70) return { bg: `hsl(${60 - (pct - 40) * 2}, 80%, 45%)`, pct, label };
  return { bg: `hsl(${Math.max(0, 20 - (pct - 70) * 0.7)}, 85%, 45%)`, pct, label };
}

function ClosingDateHeatCell({ value }) {
  if (!value) return <span className="text-sm text-zinc-400">—</span>;
  const u = getUrgencyColor(value);
  const fmt = new Date(value).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <div className="relative rounded-md overflow-hidden min-w-[180px]" title={`${fmt} — ${u.label}`}>
      <div className="absolute inset-0 rounded-md" style={{ backgroundColor: u.bg, opacity: 0.12 }} />
      <div className="absolute left-0 top-0 bottom-0 rounded-md" style={{ width: `${u.pct}%`, backgroundColor: u.bg, opacity: 0.25 }} />
      <span className="relative z-10 block px-2.5 py-1.5 text-xs font-semibold whitespace-nowrap tabular-nums" style={{ color: u.bg }}>
        {fmt}
      </span>
    </div>
  );
}

export default function RFxPage() {
  const { systemSettings } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewTab = searchParams.get('tab') || 'dashboard';
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const fmtTz = (d) => d ? fmtTs(d, tz) : '—';

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Sync filter state when URL searchParams change (e.g., from dashboard drill-down)
  useEffect(() => {
    const sp_status = searchParams.get('status') || '';
    const sp_type = searchParams.get('type') || '';
    const sp_search = searchParams.get('search') || '';
    if (sp_status !== statusFilter) setStatusFilter(sp_status);
    if (sp_type !== typeFilter) setTypeFilter(sp_type);
    if (sp_search !== search) setSearch(sp_search);
    setPage(1);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDashboardFilter = !!(searchParams.get('status') || searchParams.get('type') || searchParams.get('search'));
  const filterLabel = searchParams.get('label') || '';
  const clearDashboardFilters = () => {
    setStatusFilter(''); setTypeFilter(''); setSearch('');
    setSearchParams({ tab: 'events' });
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyTitle, setCopyTitle] = useState('');
  const [copyType, setCopyType] = useState('');
  const [typeChooserOpen, setTypeChooserOpen] = useState(false);
  const [newEventType, setNewEventType] = useState('RFP');

  useEffect(() => {
    const ref = searchParams.get('open') || searchParams.get('edit');
    if (ref) openEdit(ref);
  }, [searchParams]); // eslint-disable-line

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const r = await api.get('/api/rfx/events', { params });
      setEvents(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error('Failed to load events'); }
    setLoading(false);
  }, [page, typeFilter, statusFilter, search]);

  // Separate fetch for active event counts (all types, unfiltered)
  const [activeCounts, setActiveCounts] = useState({ RFI: 0, RFQ: 0, RFP: 0, REVERSE_AUCTION: 0, total: 0 });
  const loadActiveCounts = useCallback(async () => {
    try {
      const r = await api.get('/api/rfx/events', { params: { status: 'OPEN', page_size: 200 } });
      const all = r.data.data || [];
      setActiveCounts({
        RFI: all.filter(e => e.type === 'RFI').length,
        RFQ: all.filter(e => e.type === 'RFQ').length,
        RFP: all.filter(e => e.type === 'RFP').length,
        REVERSE_AUCTION: all.filter(e => e.type === 'REVERSE_AUCTION').length,
        total: all.length,
      });
    } catch { }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadActiveCounts(); }, [loadActiveCounts]);

  const [sortState, setSortState] = useState({ field: 'event_ref', dir: 'desc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);

  // Sort events client-side since the backend doesn't support sort param for RFx
  const sortedEvents = useMemo(() => {
    const arr = [...events];
    const dir = sortState.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = a[sortState.field] ?? '';
      const vb = b[sortState.field] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return arr;
  }, [events, sortState]);

  const rfxCols = useMemo(() => [
    { key: 'event_ref', label: 'Ref', width: 110, sortField: 'event_ref', visible: true },
    { key: 'type', label: 'Type', width: 95, sortField: 'type', visible: true },
    { key: 'title', label: 'Title', width: 260, sortField: 'title', visible: true },
    { key: 'status', label: 'Status', width: 100, sortField: 'status', visible: true },
    { key: 'created_at', label: 'Created', width: 150, sortField: 'created_at', visible: true },
    { key: 'closing_date', label: 'Closing Date', width: 150, sortField: 'closing_date', visible: true },
    { key: 'question_count', label: 'Qs', width: 50, sortField: 'question_count', visible: true },
    { key: 'supplier_count', label: 'Suppliers', width: 80, sortField: 'supplier_count', visible: true },
    { key: 'sealed', label: 'Sealed', width: 60, visible: true },
    { key: 'actions', label: '', width: 110, fixed: true, visible: true },
  ], []);



  const handleDelete = async (ref) => {
    toast(`Delete ${ref}?`, {
      description: 'This will permanently remove this event. This action cannot be undone.',
      duration: 10000,
      action: {
        label: 'Confirm Delete',
        onClick: async () => {
          try {
            await api.delete(`/api/rfx/events/${ref}`);
            toast.success(`${ref} deleted successfully`);
            loadEvents();
          } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Delete failed — this event may be in use.');
          }
        },
      },
      cancel: { label: 'Cancel', onClick: () => {} },
    });
  };

  const handlePublish = async (ref) => {
    try {
      await api.post(`/api/rfx/events/${ref}/publish`);
      toast.success(`${ref} published`);
      loadEvents();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Publish failed');
    }
  };

  const handleCopy = async () => {
    if (!copySource) return;
    try {
      const r = await api.post(`/api/rfx/events/${copySource.event_ref}/copy`, {
        title: copyTitle || `Copy of ${copySource.title}`,
        type: copyType || copySource.type,
      });
      toast.success(`Created ${r.data.event_ref}`);
      setCopyOpen(false);
      loadEvents();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Copy failed');
    }
  };

  const openEdit = async (ref) => {
    try {
      const r = await api.get(`/api/rfx/events/${ref}`);
      setEditEvent(r.data);
      setEditOpen(true);
    } catch { toast.error('Failed to load event'); }
  };

  const openCopy = (ev) => {
    setCopySource(ev);
    setCopyTitle(`Copy of ${ev.title}`);
    setCopyType(ev.type);
    setCopyOpen(true);
  };

  return (
    <AppShell>
      <div className="space-y-4 p-4 sm:p-6" data-testid="rfx-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <FileQuestion className="w-6 h-6 text-indigo-600" /> RFx Sourcing
              <HelpButton guideId="rfx-sourcing" label="RFx Sourcing Guide" />
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage RFIs, RFQs, RFPs and Auctions</p>
          </div>
          <div className="flex items-center gap-2">
            <a href="/public_RFX" target="_blank" rel="noopener noreferrer"
              className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50 transition-colors"
              data-testid="view-public-portal-link">
              <Eye className="w-3.5 h-3.5" /> View Public Portal
            </a>
            <Button onClick={() => navigate('/rfq/create')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-rfx-btn">
              <Plus className="w-4 h-4 mr-1" /> New Event
            </Button>
          </div>
        </div>

        <div className="flex gap-1 border-b" data-testid="rfx-tabs">
          {[{ id: 'dashboard', label: 'Analytics', icon: BarChart3 }, { id: 'events', label: 'Events' }, { id: 'reports', label: 'Reports', icon: FileText }].map(t => (
            <button key={t.id} onClick={() => setSearchParams({ tab: t.id })} data-testid={`rfx-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              {t.icon && <t.icon className="h-3.5 w-3.5" />} {t.label}
            </button>
          ))}
        </div>

        {viewTab === 'dashboard' && <RFxDashboard />}
        {viewTab === 'reports' && <ModuleReports reports={RFX_REPORTS} apiPrefix="/api/module-reports/rfx" moduleLabel="RFx Sourcing" />}

        {viewTab === 'events' && (<>

        {hasDashboardFilter && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 mb-3 mt-2">
            <BarChart3 className="h-3.5 w-3.5" />
            <span className="font-medium">Filtered{filterLabel ? `: ${filterLabel}` : ''}</span>
            <button onClick={clearDashboardFilters} className="ml-1 hover:text-blue-900 font-bold" data-testid="clear-rfx-filter">&times;</button>
          </div>
        )}

        {activeCounts.total > 0 && (
            <div className="bg-gradient-to-r from-indigo-50 via-violet-50 to-rose-50 border border-indigo-200 rounded-lg px-4 py-2.5 flex items-center justify-between"
              data-testid="active-events-banner">
              <div className="flex items-center gap-3">
                <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" /></span>
                <span className="font-semibold text-sm text-slate-700">Active Events:</span>
                {activeCounts.RFI > 0 && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{activeCounts.RFI} RFI{activeCounts.RFI !== 1 ? 's' : ''}</span>}
                {activeCounts.RFQ > 0 && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">{activeCounts.RFQ} RFQ{activeCounts.RFQ !== 1 ? 's' : ''}</span>}
                {activeCounts.RFP > 0 && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{activeCounts.RFP} RFP{activeCounts.RFP !== 1 ? 's' : ''}</span>}
                {activeCounts.REVERSE_AUCTION > 0 && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{activeCounts.REVERSE_AUCTION} Auction{activeCounts.REVERSE_AUCTION !== 1 ? 's' : ''}</span>}
              </div>
            </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input placeholder="Search events..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 h-9 text-sm" data-testid="rfx-search" />
          </div>
          {['', 'RFI', 'RFQ', 'RFP', 'REVERSE_AUCTION'].map(t => (
            <div key={t} className="relative group">
              <Button variant={typeFilter === t ? 'default' : 'outline'} size="sm"
                onClick={() => { setTypeFilter(t); setPage(1); }}
                className={typeFilter === t ? 'bg-indigo-600 text-white' : ''} data-testid={`filter-type-${t || 'all'}`}>
                {t ? TYPE_LABELS[t] : 'All Types'}
              </Button>
              {t && TYPE_DESCRIPTIONS[t] && (
                <div className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-colors duration-200 pointer-events-none">
                  <p className="font-semibold mb-1">{TYPE_LABELS[t]}</p>
                  <p className="leading-relaxed text-slate-300">{TYPE_DESCRIPTIONS[t]}</p>
                </div>
              )}
            </div>
          ))}
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="filter-status">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              {['DRAFT', 'OPEN', 'PAUSED', 'CLOSED', 'EVALUATION', 'AWARD_PENDING', 'AWARDED', 'CANCELLED'].map(s =>
                <SelectItem key={s} value={s}>{s}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {loading ? <LoadingSpinner /> : events.length === 0 ? (
          <EmptyState icon={FileQuestion} title="No events found" description="Create your first RFI, RFQ, RFP, or Reverse Auction" />
        ) : (
          <InteractiveTable
            tableId="rfx-events-cols"
            defaultCols={rfxCols}
            data={sortedEvents}
            rowKey="event_ref"
            sortState={sortState}
            onSort={handleSort}
            onRowClick={(ev) => openEdit(ev.event_ref)}
            renderCell={(col, ev) => {
              switch (col.key) {
                case 'event_ref': return <span className="font-semibold text-zinc-900 text-sm">{ev.event_ref}</span>;
                case 'type': return <Badge className={`${TYPE_COLORS[ev.type] || ''} text-[10px]`}>{TYPE_LABELS[ev.type] || ev.type}</Badge>;
                case 'title': return <span className="text-sm text-zinc-700 truncate block">{ev.title}</span>;
                case 'status': return <Badge className={`${STATUS_COLORS[ev.status] || ''} text-[10px]`}>{ev.status}</Badge>;
                case 'created_at': return <span className="text-sm text-zinc-600 whitespace-nowrap">{fmtTz(ev.created_at)}</span>;
                case 'closing_date': return <ClosingDateHeatCell value={ev.closing_date} />;
                case 'question_count': return <span className="text-sm text-zinc-600">{ev.question_count || 0}</span>;
                case 'supplier_count': return <span className="text-sm text-zinc-600">{ev.supplier_count || 0}</span>;
                case 'sealed': return ev.sealed_bidding?.enabled ? <Lock className="w-3.5 h-3.5 text-amber-600 mx-auto" /> : <Unlock className="w-3.5 h-3.5 text-zinc-300 mx-auto" />;
                case 'actions': return (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {ev.status === 'DRAFT' && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600 hover:text-emerald-700"
                        onClick={() => handlePublish(ev.event_ref)} title="Publish" data-testid={`publish-${ev.event_ref}`}>
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openCopy(ev)} title="Copy"
                      data-testid={`copy-${ev.event_ref}`}><Copy className="w-3.5 h-3.5" /></Button>
                    {ev.status === 'DRAFT' && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(ev.event_ref)} title="Delete" data-testid={`delete-${ev.event_ref}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                );
                default: return <span className="text-sm text-zinc-600">{ev[col.key] != null ? String(ev[col.key]) : '—'}</span>;
              }
            }}
          />
        )}

        {total > 25 && (
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>Showing {(page-1)*25+1}–{Math.min(page*25, total)} of {total}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page<=1} onClick={() => setPage(p=>p-1)}>Prev</Button>
              <Button size="sm" variant="outline" disabled={page*25>=total} onClick={() => setPage(p=>p+1)}>Next</Button>
            </div>
          </div>
        )}

        {editOpen && <RFxEditDialog open={editOpen} onClose={() => { setEditOpen(false); loadEvents(); }}
          event={editEvent} fmtTz={fmtTz} defaultType={newEventType} />}

        <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
          <DialogContent className="max-w-md" data-testid="copy-dialog">
            <DialogHeader><DialogTitle>Copy {copySource?.event_ref}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>New Title</Label><Input value={copyTitle} onChange={e => setCopyTitle(e.target.value)} data-testid="copy-title" /></div>
              <div><Label>New Type</Label>
                <Select value={copyType} onValueChange={setCopyType}>
                  <SelectTrigger data-testid="copy-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCopyOpen(false)}>Cancel</Button>
                <Button onClick={handleCopy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="copy-confirm-btn">Create Copy</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={typeChooserOpen} onOpenChange={setTypeChooserOpen}>
          <DialogContent className="max-w-lg" data-testid="type-chooser-dialog">
            <DialogHeader><DialogTitle>Create New Sourcing Event</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500 mb-4">What type of event would you like to create?</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { type: 'RFI', icon: FileQuestion, color: 'border-blue-300 hover:bg-blue-50', badge: 'bg-blue-100 text-blue-700',
                  desc: 'Gather information about supplier capabilities before issuing a formal solicitation.' },
                { type: 'RFQ', icon: FileText, color: 'border-violet-300 hover:bg-violet-50', badge: 'bg-violet-100 text-violet-700',
                  desc: 'Invite suppliers to submit competitive pricing for clearly defined goods or services.' },
                { type: 'RFP', icon: FileText, color: 'border-amber-300 hover:bg-amber-50', badge: 'bg-amber-100 text-amber-700',
                  desc: 'Invite comprehensive proposals with technical approach, methodology, and pricing.' },
                { type: 'REVERSE_AUCTION', icon: Gavel, color: 'border-rose-300 hover:bg-rose-50', badge: 'bg-rose-100 text-rose-700',
                  desc: 'Real-time competitive bidding where suppliers lower their prices within a time window.' },
              ].map(opt => (
                <button key={opt.type} onClick={() => { setTypeChooserOpen(false); setNewEventType(opt.type); setEditEvent(null); setEditOpen(true); }}
                  className={`border-2 rounded-xl p-4 text-left transition-colors hover:shadow-md ${opt.color}`}
                  data-testid={`create-type-${opt.type}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <opt.icon className="w-5 h-5" />
                    <Badge className={`text-xs ${opt.badge}`}>{TYPE_LABELS[opt.type]}</Badge>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">{opt.desc}</p>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        </>)}
      </div>
    </AppShell>
  );
}
